import React, { FC, useEffect, useState } from 'react';
import { LandscapeDocumentsTemplate } from '../../templates';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  deleteLandscapeDocument,
  fetchLandscapeDocumentById,
  fetchLandscapeDocumentsByLandscapeId,
  saveLandscapeDocument,
  updateLandscapeDocument,
} from '../../../store/landscapeAPI/landscape/reducers/landscape-document.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import { FileMeta } from '../../../store/mediaAPI/document/dto/request/document-save-request';
import { DocumentSaveResponseDto } from '../../../store/mediaAPI/document/dto/response/document-save-response';
import { saveDocument } from '../../../store/mediaAPI/document/reducers/document.slice';
import { LandscapeDocumentSaveRequestDto } from '../../../store/landscapeAPI/landscape/dto/request/landscape-document-save-request.dto';
import { LandscapeDocumentEntityType } from '../../../store/landscapeAPI/landscape/landcscape-document-entity-type';
import { getUUID } from '../../../utils/UUID';
import { LandscapeDocumentResponseDto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-document-response.dto';
import { EditLandscapeDocumentModal } from '../../organisms';
import { LandscapeDocumentUpdateRequestDto } from '../../../store/landscapeAPI/landscape/dto/request/landscape-document-update-request.dto';
import cn from 'classnames';
import styles from './LandscapeDocumentsPage.module.css';
import { LocalesLanguagesConstants } from '../../../locales/locales';

interface LandscapeDocumentsPageProps {}

const LandscapeDocumentsPage: FC<LandscapeDocumentsPageProps> = () => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const [file, setFile] = useState<string>();
  const [fileMeta, setFileMeta] = useState<FileMeta>();
  const [fileName, setFileName] = useState<string>();
  const [documentToEdit, setDocumentToEdit] =
    useState<LandscapeDocumentResponseDto>();
  const [showModal, setShowModal] = useState<boolean>(false);

  const { landscapeDocuments, status } = useSelector(
    (state: RootState) => state.landscapeDocumentSlice
  );

  const handleEditAction = async (id: string) => {
    const documentResponse = (
      await dispatch(
        fetchLandscapeDocumentById({
          id,
          language: LocalesLanguagesConstants.en,
        })
      )
    ).payload as LandscapeDocumentResponseDto;
    setDocumentToEdit(documentResponse);
    setShowModal(true);
  };

  const handleOnChange = async (document: LandscapeDocumentResponseDto) => {
    setDocumentToEdit(document);
  };

  const handleEdit = async (document: LandscapeDocumentResponseDto) => {
    const documentToUpdate: LandscapeDocumentUpdateRequestDto = {
      compactId: document.compactId,
      file: document.file,
      name: document.name,
      description: document.description,
      documentDate: document.documentDate,
      entityId: document.entityId,
      entityType: document.entityType,
      id: document.id,
      published: document.published,
    };
    await dispatch(
      updateLandscapeDocument({
        document: documentToUpdate,
        language: LocalesLanguagesConstants.en,
      })
    );
    if (id) {
      dispatch(fetchLandscapeDocumentsByLandscapeId(id));
    }
    setShowModal(false);
  };

  const handleDownload = async (documentId: string) => {
    const documentToDownload = (
      await dispatch(
        fetchLandscapeDocumentById({
          id: documentId,
          language: LocalesLanguagesConstants.en,
        })
      )
    ).payload as LandscapeDocumentResponseDto;

    const link = document.createElement('a');
    const fileUrl = documentToDownload.file;
    link.href = documentToDownload.file;
    link.download = documentToDownload.name;
    link.target = '_blank';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(fileUrl);
  };

  const handleDelete = async (documentId: string) => {
    if (id) {
      await dispatch(
        deleteLandscapeDocument({ existingId: documentId, compactId: id })
      );
      dispatch(fetchLandscapeDocumentsByLandscapeId(id));
    }
  };

  const handleFileOnChange = (file: string) => {
    setFile(file);
  };

  const handleFileMetaOnChange = (fileMeta: FileMeta) => {
    setFileMeta(fileMeta);
  };

  const handleFileNameOnChange = (fileName: string) => {
    setFileName(fileName);
  };

  const callbackFn = async () => {
    let fileSaveResponse: DocumentSaveResponseDto = {
      href: '',
    };
    if (file && file !== '' && fileMeta) {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([file], {
          type: fileMeta?.fileType,
        }),
        fileMeta?.fileSrc
      );
      fileSaveResponse = (await dispatch(saveDocument(formData)))
        .payload as DocumentSaveResponseDto;
    }
    const landscapeDocumentToSave: LandscapeDocumentSaveRequestDto = {
      compactId: id ?? '',
      file: fileSaveResponse.href,
      name: fileName ?? '',
      description: '',
      documentDate: '',
      entityId: id ?? '',
      entityType: LandscapeDocumentEntityType.Compact,
      id: getUUID(),
      published: true,
    };
    await dispatch(saveLandscapeDocument(landscapeDocumentToSave));
    if (id) {
      dispatch(fetchLandscapeDocumentsByLandscapeId(id));
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchLandscapeDocumentsByLandscapeId(id));
    }
  }, [id, dispatch]);

  if (status === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.landscapeDocumentsWrapper)}>
      {showModal && documentToEdit && (
        <EditLandscapeDocumentModal
          show={showModal}
          setShow={setShowModal}
          handleEdit={handleEdit}
          handleOnChange={handleOnChange}
          document={documentToEdit}
        />
      )}
      <LandscapeDocumentsTemplate
        data={landscapeDocuments ?? []}
        handleDownload={handleDownload}
        handleEditAction={handleEditAction}
        handleDelete={handleDelete}
        handleFileOnChange={handleFileOnChange}
        handleFileMetaOnChange={handleFileMetaOnChange}
        handleFileNameOnChange={handleFileNameOnChange}
        callbackFn={callbackFn}
      />
    </div>
  );
};

export default LandscapeDocumentsPage;
